'use client';

import { createContext, ReactElement, ReactNode, useContext } from 'react';
import { TUseCustomerConfidence, useCustomerConfidence } from './useCustomerConfidence';

const CustomerConfidenceContext = createContext<TUseCustomerConfidence | undefined>(undefined);

export type TCustomerConfidenceProviderProps = {
  customerId?: string;
  /**
   * Load-bearing. The document level is only meaningful against an organisation's required document
   * count, so the endpoint is organisation-scoped and there is no unscoped answer to fall back to.
   */
  organisationId?: string;
  /**
   * Called when the request fails. This package has no logging surface of its own, so without it a
   * failure is visible on screen but unobservable anywhere else.
   *
   * One handler for one request, rather than one per consumer, which is the point of hoisting the
   * call: the failure is a fact about the page's request, not about either region that shows it.
   */
  onError?: (error: unknown) => void;
  children: ReactNode;
};

/**
 * Fetches the customer's confidence levels once, for every region of the page that shows them.
 *
 * **Why a provider rather than a hook in each consumer.** The document level appears twice on the
 * customer page, and the two appearances are the summary and the detail of one fact: the
 * `Identity document(s)` check card near the top, and the `KYC/KYB` section's summary row further
 * down. They are separated by other content, so no single component can own both, and each calling
 * `useCustomerConfidence` would issue the same request twice.
 *
 * **The reason is not correctness.** Two calls to this endpoint with the same customer and
 * organisation return the same answer, so they cannot disagree on the level. What they differ on is
 * arrival: one region lights up while the other still shows a skeleton, which reads as the page
 * telling a client two things about one check. Fetching once makes them move in step by
 * construction, and it puts the coupling in the code rather than leaving it implicit in two hooks
 * that happen to call the same URL.
 *
 * Follows `WorkflowMetadataProvider`, whose own reason for existing is that metadata "is fetched
 * only once and shared across all wizard steps".
 *
 * **Consumers of this context are views, not components**, even though they issue no request
 * themselves. A component can be rendered by an application that knows nothing about the person
 * API; a consumer of this context cannot, because it needs this provider above it. That is the same
 * position ADR 74 takes, and the reason `DocumentTypeSelectionView`, `FileUploaderView` and
 * `PhotoGuideView` are views despite having no API import between them.
 */
export function CustomerConfidenceProvider({
  customerId,
  organisationId,
  onError,
  children,
}: Readonly<TCustomerConfidenceProviderProps>): ReactElement {
  const confidence = useCustomerConfidence({ customerId, organisationId, onError });

  return (
    <CustomerConfidenceContext.Provider value={confidence}>
      {children}
    </CustomerConfidenceContext.Provider>
  );
}

/**
 * The customer's confidence levels, from the provider above.
 *
 * **Throws when there is no provider**, rather than falling back to fetching or to a loading state.
 * A silent fallback would give this one behaviour in stories and another on the page, and the two
 * would drift without anything failing. A missing provider is a wiring mistake and should read as
 * one at the first render rather than as a card that never leaves its skeleton.
 */
export function useCustomerConfidenceContext(): TUseCustomerConfidence {
  const confidence = useContext(CustomerConfidenceContext);

  if (!confidence) {
    throw new Error(
      'useCustomerConfidenceContext must be used within a CustomerConfidenceProvider.',
    );
  }

  return confidence;
}
